import React, { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { checkEmailTypo } from '@/utils/emailTypoCheck';

interface EmailInputWithSuggestionProps
  extends Omit<React.ComponentProps<typeof Input>, 'onChange' | 'type'> {
  value: string;
  onEmailChange: (email: string) => void;
}

export function EmailInputWithSuggestion({
  value,
  onEmailChange,
  onBlur,
  ...inputProps
}: EmailInputWithSuggestionProps) {
  const [suggestion, setSuggestion] = useState<string | null>(null);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const typoSuggestion = checkEmailTypo(e.target.value);
      setSuggestion(typoSuggestion);
      onBlur?.(e);
    },
    [onBlur]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onEmailChange(e.target.value);
      // Clear suggestion when user starts typing again
      if (suggestion) setSuggestion(null);
    },
    [onEmailChange, suggestion]
  );

  const acceptSuggestion = useCallback(() => {
    if (suggestion) {
      onEmailChange(suggestion);
      setSuggestion(null);
    }
  }, [suggestion, onEmailChange]);

  const dismissSuggestion = useCallback(() => {
    setSuggestion(null);
  }, []);

  return (
    <div>
      <Input
        type="email"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        {...inputProps}
      />
      {suggestion && (
        <div className="mt-1.5 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          <span>
            Did you mean <strong>{suggestion}</strong>?
          </span>
          <span className="ml-2 inline-flex gap-1">
            <button
              type="button"
              onClick={acceptSuggestion}
              className="font-medium underline hover:text-amber-900"
            >
              Yes, fix it
            </button>
            <span className="text-amber-400">|</span>
            <button
              type="button"
              onClick={dismissSuggestion}
              className="font-medium underline hover:text-amber-900"
            >
              No, ignore
            </button>
          </span>
        </div>
      )}
    </div>
  );
}
